import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { BatteryTypeOption, toBatteryTypeFilter, toBatteryTypeSeries } from 'src/app/_shared/utils/battery-type';
import { KmToMilesPipe } from 'src/app/_shared/pipes/km-to-miles.pipe';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';

@Component({
  selector: 'app-vehicle-mileage-chart',
  templateUrl: './vehicle-mileage-chart.component.html',
  styleUrls: ['./vehicle-mileage-chart.component.scss']
})
export class VehicleMileageChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() selectedBatteryTypes: BatteryTypeOption[] = [];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  chartRange!: { range: [number, number]; };
  private readonly kmToMilesPipe = new KmToMilesPipe();

  constructor(
    private readonly fleetGeneralFigureService: FleetGeneralFigureService
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetGeneralFigureService.getMileageFleet(this.fleetId, toBatteryTypeFilter(this.selectedBatteryTypes)).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          this.plotVehicleMileage(datapoints);
        },
        error: (error) => {
          console.error('Error fetching Vehicle Mileage Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        },
        complete: () => {
          this.dataLoading = false;
        }
      }
    )
  }

  plotVehicleMileage(datapoints: any) {
    const transformXAxisPipe = new TransformXAxisPipe();
    const data = toBatteryTypeSeries(datapoints, this.selectedBatteryTypes).map((series) => ({
      x: this.getConvertedValuesByUnit(series.datapoints.map((dataObject: any) => dataObject.binMileage)),
      y: series.datapoints.map((dataObject: any) => dataObject.percVeh),
      type: 'bar',
      name: series.label,
      marker: {
        color: series.color
      }
    }));
    this.chartRange = transformXAxisPipe.transform(data.flatMap((trace) => trace.x), data.flatMap((trace) => trace.y), 5000, 1000000);

    const layout = {
      title: 'Vehicle Mileage',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: `Mileage [${this.getMileageUnit()}]`,
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Percentage of Fleet Vehicles [%]'
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('vehicle-mileage-chart', data, layout);
  }

  getConvertedValuesByUnit(arr: number[]): number[] {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? arr : arr.map(val => this.kmToMilesPipe.transform(val));
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? arr.map(val => this.kmToMilesPipe.transform(val))
        : arr;
    }
  }

  getMileageUnit(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'km' : 'mi';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'mi'
        : 'km';
    }
  }

}